import { useState } from 'react'
import {
  LayoutGrid, Users, GitMerge, FileCode2, Lock, Bell,
  Palette, Settings, ToggleLeft, ToggleRight, Plus, Trash2,
  Edit3, Save, X, Shield, Check, AlertTriangle,
  Rocket, GitBranch, Server, Bot, Layers, Network, Globe,
  ShieldCheck, Key, Headphones, Monitor, TrendingDown, BarChart3, Radio,
  Package, Eye, EyeOff, Upload, Download, Database,
  ChevronDown, ChevronRight,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { ProductPoolTiers } from '@/components/admin/ProductPoolTiers'
import { POOL_TIERS } from '@/data/sizeCatalog'
import { usesPoolTiers } from '@/utils/sizing'
import { deploymentsApi } from '@/services/deployments'
import type { ErpVersion } from '@/services/deployments'
import {
  useRBACStore, type RBACRole, ROLE_LABELS, ROLE_DESCRIPTIONS,
  DEFAULT_TILE_PERMISSIONS, hasMinRole,
} from '@/stores/rbacStore'
import { formatMaxUsers, parseMaxUsersInput, readMaxUsers } from '@/utils/maxUsers'
import styles from './AdminPage.module.css'
import {
  VM_FORM_KEYS, vmFormFromResources, vmPayloadFromForm, validateVmSizingForm, vmSummary, type VmSizingFormValues,
} from '@/components/apprise-vm/vmSizingForm'
import { AdminVmSizingBlock } from '@/components/apprise-vm/AdminVmSizingBlock'
import { productKeys } from '@/hooks/useProducts'
import { osDiskLabel } from '@/components/apprise-vm/appriseReview'
import { isAppriseProduct } from '@/utils/apprise'
import { AppriseImageCatalog } from '@/components/apprise-vm/AppriseImageCatalog'

/* ─── Tab definitions ────────────────────────────────────────────────────── */
type AdminTab =
  | 'tiles'
  | 'rbac'
  | 'workflows'
  | 'templates'
  | 'secrets'
  | 'notifications'
  | 'branding'
  | 'platform'

const TABS: { id: AdminTab; label: string; icon: React.ElementType; desc: string }[] = [
  { id: 'tiles',         label: 'Tile Management',    icon: LayoutGrid, desc: 'Add, configure and order dashboard tiles'  },
  { id: 'rbac',          label: 'RBAC & Permissions', icon: Users,      desc: 'Roles, permissions and user assignments'    },
  { id: 'workflows',     label: 'Workflows',          icon: GitMerge,   desc: 'Approval workflows and automation gates'    },
  { id: 'templates',     label: 'Deploy Templates',   icon: FileCode2,  desc: 'Reusable deployment configuration templates'},
  { id: 'secrets',       label: 'Secrets & Config',   icon: Lock,       desc: 'Environment variables and secret values'   },
  { id: 'notifications', label: 'Notifications',      icon: Bell,       desc: 'Email, Teams, Slack and webhook setup'     },
  { id: 'branding',      label: 'Branding & Theme',   icon: Palette,    desc: 'Logo, colors and white-label config'       },
  { id: 'platform',      label: 'Platform Config',    icon: Settings,   desc: 'T-shirt sizes, products and quotas'        },
]

/* ─── Mock data ──────────────────────────────────────────────────────────── */
interface TileConfig {
  id: string
  title: string
  icon: React.ElementType
  iconName?: string
  route: string
  category: string
  enabled: boolean
  roles: string[]
  endpointOverride?: string
}

const TILE_CATALOG: TileConfig[] = [
  { id: 'new-env',        title: 'New Environment Deployment', icon: Rocket,     route: '/deployments/new',     category: 'deployments',    enabled: true,  roles: ['admin','platform_engineer','devops'] },
  { id: 'release-mgmt',   title: 'Release Management',         icon: GitBranch,  route: '/release-management',  category: 'operations',     enabled: true,  roles: ['admin','release_manager','devops']   },
  { id: 'status-central', title: 'Status Central',             icon: Radio,      route: '/status-central',      category: 'observability',  enabled: true,  roles: ['admin','viewer','devops']            },
  { id: 'mcp-deploy',     title: 'MCP Server Deployment',      icon: Server,     route: '/mcp-deployment',      category: 'deployments',    enabled: true,  roles: ['admin','platform_engineer']          },
  { id: 'agent-deploy',   title: 'Agent Deployment',           icon: Bot,        route: '/agent-deployment',    category: 'deployments',    enabled: true,  roles: ['admin','platform_engineer']          },
  { id: 'app-in-aks',     title: 'App in Existing AKS',        icon: Layers,     route: '/app-in-aks',          category: 'deployments',    enabled: true,  roles: ['admin','devops']                     },
  { id: 'haproxy',        title: 'HAProxy Configuration',      icon: Network,    route: '/haproxy',             category: 'infrastructure', enabled: true,  roles: ['admin','infra_engineer']             },
  { id: 'cloudflare',     title: 'Cloudflare Configuration',   icon: Globe,      route: '/cloudflare',          category: 'infrastructure', enabled: true,  roles: ['admin','infra_engineer']             },
  { id: 'patch-mgmt',     title: 'Patch Management',           icon: ShieldCheck,route: '/patch-management',    category: 'operations',     enabled: true,  roles: ['admin','devops','infra_engineer']    },
  { id: 'certificates',   title: 'Certificate Management',     icon: Key,        route: '/certificates',        category: 'infrastructure', enabled: true,  roles: ['admin','infra_engineer']             },
  { id: 'service-desk',   title: 'Service Desk',               icon: Headphones, route: '/service-desk',        category: 'support',        enabled: true,  roles: ['admin','viewer','devops']            },
  { id: 'monitoring',     title: 'Live Monitoring',            icon: Monitor,    route: '/monitoring',          category: 'observability',  enabled: true,  roles: ['admin','viewer','devops']            },
  { id: 'cost-optimize',  title: 'Cost Optimization',          icon: TrendingDown,route: '/cost-optimization',  category: 'financial',      enabled: true,  roles: ['admin','finops']                     },
  { id: 'cost-reports',   title: 'Cost Reports',               icon: BarChart3,  route: '/cost-reports',        category: 'financial',      enabled: true,  roles: ['admin','finops','viewer']            },
  { id: 'cmdb',           title: 'CMDB',                       icon: Database,   route: '/cmdb',                category: 'observability',  enabled: true,  roles: ['admin','viewer','devops']            },
]

const ALL_ROLES = ['admin', 'platform_engineer', 'devops', 'infra_engineer', 'release_manager', 'finops', 'viewer']

const ICON_MAP: Record<string, React.ElementType> = {
  Rocket, GitBranch, Server, Bot, Layers, Network, Globe,
  ShieldCheck, Key, Headphones, Monitor, TrendingDown, BarChart3, Radio,
  Package, Database, Shield, Settings, LayoutGrid, Bell,
  Eye, Upload, Download, Lock, Palette, Users,
}

const CATEGORIES_LIST = ['deployments', 'operations', 'infrastructure', 'observability', 'financial', 'support']

function getIconName(icon: React.ElementType): string {
  return Object.keys(ICON_MAP).find(k => ICON_MAP[k] === icon) ?? 'Rocket'
}


interface WorkflowRule { id: string; name: string; trigger: string; approvers: string[]; minApprovals: number; enabled: boolean; slaHours: number }
const MOCK_WORKFLOWS: WorkflowRule[] = [
  { id: 'prod-deploy',   name: 'Production Deployment Approval', trigger: 'deployment.environment=production',   approvers: ['release_manager','admin'], minApprovals: 1, enabled: true,  slaHours: 4  },
  { id: 'prod-usgov',    name: 'US Gov Deployment Approval',     trigger: 'deployment.environment=prod_usgov',   approvers: ['admin'],                   minApprovals: 1, enabled: true,  slaHours: 2  },
  { id: 'patch-prod',    name: 'Production Patch Approval',      trigger: 'patch.environment=production',        approvers: ['release_manager','admin'], minApprovals: 2, enabled: true,  slaHours: 8  },
  { id: 'cert-renew',    name: 'Certificate Renewal Approval',   trigger: 'certificate.action=renew',            approvers: ['infra_engineer'],          minApprovals: 1, enabled: false, slaHours: 24 },
  { id: 'cost-change',   name: 'Budget Override Approval',       trigger: 'cost.exceeds_budget=true',            approvers: ['finops','admin'],          minApprovals: 1, enabled: true,  slaHours: 4  },
]

interface SecretEntry { id: string; key: string; value: string; scope: string; lastUpdated: string; masked: boolean }
const MOCK_SECRETS: SecretEntry[] = [
  { id: '1', key: 'GITHUB_TOKEN',         value: 'ghp_••••••••••••••••••••',      scope: 'global', lastUpdated: '2026-04-01', masked: true },
  { id: '2', key: 'AZURE_DEVOPS_TOKEN',   value: '••••••••••••••••••••••••••••', scope: 'global', lastUpdated: '2026-03-15', masked: true },
  { id: '3', key: 'CLOUDFLARE_API_TOKEN', value: '••••••••••••••••••••••••••••', scope: 'global', lastUpdated: '2026-02-20', masked: true },
  { id: '4', key: 'AZURE_SUBSCRIPTION_ID',value: 'a1b2c3d4-••••-••••-••••-••••', scope: 'global', lastUpdated: '2026-01-10', masked: true },
  { id: '5', key: 'POSTGRES_PASSWORD',    value: '••••••••••••••••••',            scope: 'prod',   lastUpdated: '2026-04-05', masked: true },
]

/* ─── Tile Form Modal ────────────────────────────────────────────────────── */
interface TileFormState {
  id?: string
  title: string
  iconName: string
  route: string
  category: string
  enabled: boolean
  roles: string[]
  endpointOverride: string
}

function emptyForm(): TileFormState {
  return { title: '', iconName: 'Rocket', route: '/', category: '', enabled: true, roles: [], endpointOverride: '' }
}

function tileToForm(tile: TileConfig): TileFormState {
  return {
    id: tile.id,
    title: tile.title,
    iconName: tile.iconName ?? getIconName(tile.icon),
    route: tile.route,
    category: tile.category,
    enabled: tile.enabled,
    roles: [...tile.roles],
    endpointOverride: tile.endpointOverride ?? '',
  }
}

interface TileFormModalProps {
  initial: TileFormState
  isEdit: boolean
  onSave: (tile: TileConfig) => void
  onClose: () => void
}

function TileFormModal({ initial, isEdit, onSave, onClose }: TileFormModalProps) {
  const [form, setForm] = useState<TileFormState>(initial)
  const patch = (p: Partial<TileFormState>) => setForm(f => ({ ...f, ...p }))

  const toggleRole = (role: string) =>
    patch({ roles: form.roles.includes(role) ? form.roles.filter(r => r !== role) : [...form.roles, role] })

  const valid = !!(form.title.trim() && form.route.trim() && form.category && form.iconName)

  const handleSave = () => {
    if (!valid) return
    const id = form.id ?? form.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    const route = form.route.startsWith('/') ? form.route.trim() : '/' + form.route.trim()
    onSave({
      id,
      title: form.title.trim(),
      icon: ICON_MAP[form.iconName] ?? Rocket,
      iconName: form.iconName,
      route,
      category: form.category,
      enabled: form.enabled,
      roles: form.roles,
      endpointOverride: form.endpointOverride.trim() || undefined,
    })
  }

  const PreviewIcon = ICON_MAP[form.iconName] ?? Rocket

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <PreviewIcon size={16} />
            {isEdit ? `Edit Tile: ${initial.title}` : 'Add New Tile'}
          </div>
          <button className={styles.modalClose} onClick={onClose}><X size={15} /></button>
        </div>

        <div className={styles.modalBody}>
          {/* Title */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Title <span className={styles.requiredMark}>*</span></label>
            <input
              className={styles.formInput}
              value={form.title}
              onChange={e => patch({ title: e.target.value })}
              placeholder="e.g. Custom Module"
              autoFocus
            />
          </div>

          {/* Route */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Route <span className={styles.requiredMark}>*</span></label>
            <input
              className={styles.formInput}
              value={form.route}
              onChange={e => patch({ route: e.target.value })}
              placeholder="/my-module"
            />
            <div className={styles.formHint}>URL path — prefix with / (e.g. /my-module)</div>
          </div>

          {/* Category + Icon */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Category <span className={styles.requiredMark}>*</span></label>
              <select
                className={styles.formSelect}
                value={form.category}
                onChange={e => patch({ category: e.target.value })}
              >
                <option value="">— select —</option>
                {CATEGORIES_LIST.map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Icon <span className={styles.requiredMark}>*</span></label>
              <select
                className={styles.formSelect}
                value={form.iconName}
                onChange={e => patch({ iconName: e.target.value })}
              >
                {Object.keys(ICON_MAP).map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Endpoint Override */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              API Endpoint Override <span className={styles.optionalHint}>(optional)</span>
            </label>
            <input
              className={styles.formInput}
              value={form.endpointOverride}
              onChange={e => patch({ endpointOverride: e.target.value })}
              placeholder="/api/v1/custom-module"
            />
          </div>

          {/* Roles */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Accessible Roles</label>
            <div className={styles.roleCheckGrid}>
              {ALL_ROLES.map(role => (
                <label key={role} className={styles.roleCheckItem}>
                  <input
                    type="checkbox"
                    checked={form.roles.includes(role)}
                    onChange={() => toggleRole(role)}
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Enabled */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Visibility</label>
            <label className={styles.roleCheckItem} style={{ width: 'fit-content' }}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={e => patch({ enabled: e.target.checked })}
              />
              <span>Show on dashboard</span>
            </label>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose}><X size={13} /> Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={!valid}>
            <Save size={13} /> {isEdit ? 'Save Changes' : 'Add Tile'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Tile Management Tab ────────────────────────────────────────────────── */
function TileManagementTab() {
  const [tiles, setTiles] = useState<TileConfig[]>(TILE_CATALOG)
  const [modal, setModal] = useState<{ form: TileFormState; isEdit: boolean } | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const toggleTile = (id: string) =>
    setTiles(ts => ts.map(t => t.id === id ? { ...t, enabled: !t.enabled } : t))

  const openAdd  = () => setModal({ form: emptyForm(), isEdit: false })
  const openEdit = (tile: TileConfig) => setModal({ form: tileToForm(tile), isEdit: true })
  const closeModal = () => setModal(null)

  const handleSave = (saved: TileConfig) => {
    setTiles(ts =>
      modal?.isEdit
        ? ts.map(t => t.id === saved.id ? saved : t)
        : [...ts, saved]
    )
    closeModal()
  }

  const handleDelete = () => {
    if (deleteId) setTiles(ts => ts.filter(t => t.id !== deleteId))
    setDeleteId(null)
  }

  const categories = [...new Set(tiles.map(t => t.category))]

  return (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}>Dashboard Tile Configuration</div>
          <div className={styles.tabDesc}>Enable, disable, and configure access permissions for each module tile</div>
        </div>
        <div className={styles.tabActions}>
          <span className={styles.statChip}>
            {tiles.filter(t => t.enabled).length} / {tiles.length} enabled
          </span>
          <button className={styles.btnPrimary} onClick={openAdd}><Plus size={14} /> Add Tile</button>
        </div>
      </div>

      {categories.map(cat => (
        <div key={cat} className={styles.tileGroup}>
          <div className={styles.tileGroupHeader}>
            <span className={styles.tileGroupLabel}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</span>
            <span className={styles.tileGroupCount}>{tiles.filter(t => t.category === cat).length} tiles</span>
          </div>

          <div className={styles.tileTable}>
            <div className={styles.tileTableHeader}>
              <span>Module</span>
              <span>Route</span>
              <span>Roles</span>
              <span>Status</span>
              <span>Actions</span>
            </div>

            {tiles.filter(t => t.category === cat).map(tile => {
              const Icon = tile.icon
              const isDeleting = deleteId === tile.id
              return (
                <div key={tile.id} className={`${styles.tileRow} ${!tile.enabled ? styles.tileRowDisabled : ''}`}>
                  <div className={styles.tileRowName}>
                    <Icon size={15} />
                    <span>{tile.title}</span>
                  </div>
                  <code className={styles.tileRoute}>{tile.route}</code>
                  <div className={styles.tileRoles}>
                    {tile.roles.slice(0, 2).map(r => (
                      <span key={r} className={styles.roleChip}>{r}</span>
                    ))}
                    {tile.roles.length > 2 && (
                      <span className={styles.roleChipMore}>+{tile.roles.length - 2}</span>
                    )}
                  </div>
                  <div className={styles.tileToggle}>
                    <button
                      className={`${styles.toggleBtn} ${tile.enabled ? styles.toggleOn : styles.toggleOff}`}
                      onClick={() => toggleTile(tile.id)}
                    >
                      {tile.enabled
                        ? <><ToggleRight size={18} /> Active</>
                        : <><ToggleLeft size={18} /> Disabled</>
                      }
                    </button>
                  </div>
                  <div className={styles.tileRowActions}>
                    {isDeleting ? (
                      <div className={styles.deleteConfirm}>
                        <AlertTriangle size={12} style={{ color: 'var(--aptean-error)', flexShrink: 0 }} />
                        <span className={styles.deleteConfirmText}>Delete?</span>
                        <button className={styles.btnConfirmDelete} onClick={handleDelete}>Yes</button>
                        <button className={styles.btnCancelDelete} onClick={() => setDeleteId(null)}>No</button>
                      </div>
                    ) : (
                      <>
                        <button
                          className={styles.iconActionBtn}
                          title="Edit tile"
                          onClick={() => openEdit(tile)}
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          className={`${styles.iconActionBtn} ${styles.iconDanger}`}
                          title="Delete tile"
                          onClick={() => setDeleteId(tile.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {modal && (
        <TileFormModal
          initial={modal.form}
          isEdit={modal.isEdit}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

/* ─── RBAC Tab (fully functional) ───────────────────────────────────────── */
const RBAC_ROLES: RBACRole[] = ['admin', 'contributor', 'viewer', 'reader']

const ROLE_COLORS: Record<RBACRole, string> = {
  admin:       'var(--aptean-error)',
  contributor: 'var(--dc-ops)',
  viewer:      'var(--aptean-teal)',
  reader:      'var(--aptean-text-muted)',
}

function RBACTab() {
  const {
    tilePermissions, setTilePermission, resetPermissions,
    currentRole, setRole,
  } = useRBACStore()

  const [selectedRole, setSelectedRoleLocal] = useState<RBACRole>('admin')

  // For each tile, find its permission config
  const getPermission = (tileId: string) =>
    tilePermissions.find(p => p.tileId === tileId)
    ?? DEFAULT_TILE_PERMISSIONS.find(p => p.tileId === tileId)
    ?? { tileId, minViewRole: 'viewer' as RBACRole, minActionRole: 'contributor' as RBACRole }

  return (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}>Role-Based Access Control</div>
          <div className={styles.tabDesc}>
            Configure tile visibility and action permissions per role. Changes apply immediately to the dashboard.
          </div>
        </div>
        <div className={styles.tabActions}>
          <button className={styles.btnSecondary} onClick={resetPermissions}>
            Reset to defaults
          </button>
        </div>
      </div>

      {/* Active role simulation */}
      <div className={styles.rbacSimulator}>
        <Shield size={14} />
        <span className={styles.rbacSimLabel}>Simulate role (affects dashboard tiles):</span>
        <div className={styles.rbacSimRoles}>
          {RBAC_ROLES.map(r => (
            <button
              key={r}
              className={`${styles.rbacSimBtn} ${currentRole === r ? styles.rbacSimBtnActive : ''}`}
              style={{ '--role-color': ROLE_COLORS[r] } as React.CSSProperties}
              onClick={() => setRole(r)}
            >
              {ROLE_LABELS[r]}
            </button>
          ))}
        </div>
        <span className={styles.rbacSimHint}>{ROLE_DESCRIPTIONS[currentRole]}</span>
      </div>

      {/* Role cards overview */}
      <div className={styles.roleCardsGrid}>
        {RBAC_ROLES.map(role => {
          const visibleCount = TILE_CATALOG.filter(t => {
            const perm = getPermission(t.id)
            return hasMinRole(role, perm.minViewRole)
          }).length
          const actionCount = TILE_CATALOG.filter(t => {
            const perm = getPermission(t.id)
            return hasMinRole(role, perm.minActionRole)
          }).length
          return (
            <div
              key={role}
              className={`${styles.roleCard} ${selectedRole === role ? styles.roleCardActive : ''}`}
              style={{ '--role-color': ROLE_COLORS[role] } as React.CSSProperties}
              onClick={() => setSelectedRoleLocal(role)}
            >
              <div className={styles.roleCardHeader}>
                <div className={styles.roleCardAvatar} style={{ background: ROLE_COLORS[role] }}>
                  {ROLE_LABELS[role][0]}
                </div>
                <div>
                  <div className={styles.roleCardName}>{ROLE_LABELS[role]}</div>
                  {role === 'admin' && <Shield size={11} style={{ color: ROLE_COLORS[role] }} />}
                </div>
              </div>
              <div className={styles.roleCardDesc}>{ROLE_DESCRIPTIONS[role]}</div>
              <div className={styles.roleCardStats}>
                <div className={styles.roleCardStat}>
                  <span className={styles.roleCardStatVal}>{visibleCount}</span>
                  <span className={styles.roleCardStatLbl}>visible</span>
                </div>
                <div className={styles.roleCardStat}>
                  <span className={styles.roleCardStatVal}>{actionCount}</span>
                  <span className={styles.roleCardStatLbl}>can act</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Tile permission matrix */}
      <div className={styles.permMatrix}>
        <div className={styles.permMatrixTitle}>
          Tile Permissions — {ROLE_LABELS[selectedRole]}
        </div>
        <div className={styles.permMatrixDesc}>
          Set the minimum role required to view or perform actions on each tile.
        </div>

        <div className={styles.permMatrixHeader}>
          <span>Module</span>
          <span>Category</span>
          <span>Min. View Role</span>
          <span>Min. Action Role</span>
          <span>
            {ROLE_LABELS[selectedRole]} can view?
          </span>
          <span>
            {ROLE_LABELS[selectedRole]} can act?
          </span>
        </div>

        {TILE_CATALOG.map(tile => {
          const Icon = tile.icon
          const perm = getPermission(tile.id)
          const roleCanView = hasMinRole(selectedRole, perm.minViewRole)
          const roleCanAct  = hasMinRole(selectedRole, perm.minActionRole)

          return (
            <div key={tile.id} className={styles.permMatrixRow}>
              <div className={styles.permTileName}>
                <Icon size={14} />
                <span>{tile.title}</span>
              </div>
              <span className={styles.permCategory}>{tile.category}</span>

              {/* Min view role select */}
              <select
                className={styles.permSelect}
                value={perm.minViewRole}
                onChange={e => setTilePermission(tile.id, { minViewRole: e.target.value as RBACRole })}
                disabled={tile.id === 'cmdb' && false /* always configurable */}
              >
                {RBAC_ROLES.map(r => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>

              {/* Min action role select */}
              <select
                className={styles.permSelect}
                value={perm.minActionRole}
                onChange={e => setTilePermission(tile.id, { minActionRole: e.target.value as RBACRole })}
              >
                {RBAC_ROLES.map(r => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>

              <div className={`${styles.permCheck} ${roleCanView ? styles.permCheckYes : styles.permCheckNo}`}>
                {roleCanView ? <Check size={13} /> : <X size={13} />}
              </div>

              <div className={`${styles.permCheck} ${roleCanAct ? styles.permCheckYes : styles.permCheckNo}`}>
                {roleCanAct ? <Check size={13} /> : <X size={13} />}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ─── Workflows Tab ──────────────────────────────────────────────────────── */
function WorkflowsTab() {
  const [workflows, setWorkflows] = useState(MOCK_WORKFLOWS)

  const toggleWorkflow = (id: string) =>
    setWorkflows(ws => ws.map(w => w.id === id ? { ...w, enabled: !w.enabled } : w))

  return (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}>Approval Workflows</div>
          <div className={styles.tabDesc}>Configure automated approval gates for deployment operations</div>
        </div>
        <button className={styles.btnPrimary}><Plus size={14} /> New Workflow</button>
      </div>

      <div className={styles.workflowList}>
        {workflows.map(wf => (
          <div key={wf.id} className={`${styles.workflowCard} ${!wf.enabled ? styles.workflowDisabled : ''}`}>
            <div className={styles.workflowHeader}>
              <div className={styles.workflowTitle}>{wf.name}</div>
              <div className={styles.workflowActions}>
                <span className={`${styles.wfStatus} ${wf.enabled ? styles.wfStatusOn : styles.wfStatusOff}`}>
                  {wf.enabled ? 'Active' : 'Disabled'}
                </span>
                <button className={styles.iconActionBtn} title="Edit"><Edit3 size={13} /></button>
                <button
                  className={styles.toggleBtnSm}
                  onClick={() => toggleWorkflow(wf.id)}
                >
                  {wf.enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                </button>
              </div>
            </div>

            <div className={styles.workflowMeta}>
              <div className={styles.wfMeta}>
                <span className={styles.wfMetaLabel}>Trigger</span>
                <code className={styles.wfMetaValue}>{wf.trigger}</code>
              </div>
              <div className={styles.wfMeta}>
                <span className={styles.wfMetaLabel}>Approvers</span>
                <div className={styles.wfApprovers}>
                  {wf.approvers.map(a => <span key={a} className={styles.roleChip}>{a}</span>)}
                </div>
              </div>
              <div className={styles.wfMeta}>
                <span className={styles.wfMetaLabel}>Min. Approvals</span>
                <span className={styles.wfMetaValue}>{wf.minApprovals}</span>
              </div>
              <div className={styles.wfMeta}>
                <span className={styles.wfMetaLabel}>SLA</span>
                <span className={styles.wfMetaValue}>{wf.slaHours}h</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Secrets Tab ────────────────────────────────────────────────────────── */
function SecretsTab() {
  const [secrets, setSecrets] = useState(MOCK_SECRETS)
  const [newKey, setNewKey] = useState('')
  const [newVal, setNewVal] = useState('')

  const toggleMask = (id: string) =>
    setSecrets(ss => ss.map(s => s.id === id ? { ...s, masked: !s.masked } : s))

  return (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}>Secrets & Environment Variables</div>
          <div className={styles.tabDesc}>
            Manage platform-level secrets. Values are encrypted at rest and never logged.
          </div>
        </div>
        <button className={styles.btnPrimary}><Plus size={14} /> Add Secret</button>
      </div>

      <div className={styles.secretsNote}>
        <AlertTriangle size={14} />
        Secrets are stored encrypted and resolved via Azure Key Vault at runtime. Only masked values are shown here.
      </div>

      {/* Add form */}
      <div className={styles.secretAddRow}>
        <input
          className={styles.secretInput}
          placeholder="KEY_NAME"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          style={{ fontFamily: 'var(--aptean-font-mono)', fontWeight: 600 }}
        />
        <input
          className={styles.secretInput}
          placeholder="value"
          type="password"
          value={newVal}
          onChange={e => setNewVal(e.target.value)}
        />
        <button className={styles.btnPrimary} disabled={!newKey || !newVal}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className={styles.secretsTable}>
        <div className={styles.secretsHeader}>
          <span>Key</span>
          <span>Value</span>
          <span>Scope</span>
          <span>Last Updated</span>
          <span>Actions</span>
        </div>
        {secrets.map(s => (
          <div key={s.id} className={styles.secretRow}>
            <code className={styles.secretKey}>{s.key}</code>
            <div className={styles.secretValue}>
              <span className={s.masked ? styles.secretMasked : ''}>{s.value}</span>
              <button className={styles.secretToggle} onClick={() => toggleMask(s.id)}>
                {s.masked ? <Eye size={13} /> : <EyeOff size={13} />}
              </button>
            </div>
            <span className={styles.scopeBadge}>{s.scope}</span>
            <span className={styles.secretDate}>{s.lastUpdated}</span>
            <div className={styles.secretActions}>
              <button className={styles.iconActionBtn}><Edit3 size={13} /></button>
              <button className={`${styles.iconActionBtn} ${styles.iconDanger}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Notifications Tab ──────────────────────────────────────────────────── */
function NotificationsTab() {
  const [channels, setChannels] = useState({
    email:   { enabled: true,  value: 'platform-alerts@aptean.com' },
    teams:   { enabled: true,  value: 'https://outlook.office.com/webhook/...' },
    slack:   { enabled: false, value: '' },
    webhook: { enabled: false, value: '' },
  })

  type ChannelKey = keyof typeof channels

  return (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}>Notification Channels</div>
          <div className={styles.tabDesc}>Configure where platform alerts and deployment events are sent</div>
        </div>
      </div>

      <div className={styles.notifChannels}>
        {(Object.entries(channels) as [ChannelKey, typeof channels[ChannelKey]][]).map(([key, cfg]) => (
          <div key={key} className={`${styles.notifChannel} ${!cfg.enabled ? styles.notifChannelOff : ''}`}>
            <div className={styles.notifChannelHeader}>
              <div className={styles.notifChannelName}>
                <span className={styles.notifChannelDot} style={{
                  background: cfg.enabled ? 'var(--aptean-success)' : 'var(--aptean-text-muted)',
                }} />
                {key.charAt(0).toUpperCase() + key.slice(1)}
                {key === 'teams' && <span className={styles.recBadge}>Recommended</span>}
              </div>
              <button
                className={`${styles.toggleBtn} ${cfg.enabled ? styles.toggleOn : styles.toggleOff}`}
                onClick={() => setChannels(c => ({ ...c, [key]: { ...c[key], enabled: !c[key].enabled } }))}
              >
                {cfg.enabled
                  ? <><ToggleRight size={18} /> Enabled</>
                  : <><ToggleLeft size={18} /> Disabled</>
                }
              </button>
            </div>
            <div className={styles.notifChannelBody}>
              <label className={styles.notifChannelLabel}>
                {key === 'email' ? 'Email address' :
                 key === 'teams' ? 'Teams Webhook URL' :
                 key === 'slack' ? 'Slack Webhook URL' : 'Webhook URL'}
              </label>
              <div className={styles.notifInputRow}>
                <input
                  className={styles.notifInput}
                  type={key === 'email' ? 'email' : 'url'}
                  placeholder={
                    key === 'email' ? 'alerts@company.com' :
                    key === 'teams' ? 'https://outlook.office.com/webhook/...' :
                    key === 'slack' ? 'https://hooks.slack.com/services/...' :
                    'https://your-endpoint.com/hook'
                  }
                  value={cfg.value}
                  onChange={e => setChannels(c => ({ ...c, [key]: { ...c[key], value: e.target.value } }))}
                  disabled={!cfg.enabled}
                />
                <button className={styles.btnSecondary} disabled={!cfg.enabled}>Test</button>
                <button className={styles.btnPrimary}  disabled={!cfg.enabled}><Save size={13} /> Save</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.notifEvents}>
        <div className={styles.tabTitle} style={{ fontSize: '0.9rem' }}>Event Subscriptions</div>
        <div className={styles.notifEventGrid}>
          {[
            'Deployment Started', 'Deployment Completed', 'Deployment Failed',
            'Approval Required', 'Certificate Expiring', 'Patch Available',
            'Health Check Failed', 'Cost Budget Exceeded', 'Security Alert',
          ].map(ev => (
            <label key={ev} className={styles.notifEventItem}>
              <input type="checkbox" defaultChecked className={styles.checkbox} />
              <span>{ev}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ─── Branding Tab ───────────────────────────────────────────────────────── */
function BrandingTab() {
  const [brandName,   setBrandName]   = useState('Platform Central')
  const [primaryColor,setPrimaryColor]= useState('#54B3BE')
  const [companyName, setCompanyName] = useState('Aptean')

  return (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}>Branding & Theme</div>
          <div className={styles.tabDesc}>Customize the platform appearance and white-label settings</div>
        </div>
        <button className={styles.btnPrimary}><Save size={14} /> Save Changes</button>
      </div>

      <div className={styles.brandingGrid}>
        <div className={styles.brandingCard}>
          <div className={styles.brandingCardTitle}>Platform Identity</div>
          <div className={styles.brandingField}>
            <label className={styles.fieldLabel}>Platform Name</label>
            <input className={styles.fieldInput} value={brandName} onChange={e => setBrandName(e.target.value)} />
          </div>
          <div className={styles.brandingField}>
            <label className={styles.fieldLabel}>Company / Client Name</label>
            <input className={styles.fieldInput} value={companyName} onChange={e => setCompanyName(e.target.value)} />
          </div>
          <div className={styles.brandingField}>
            <label className={styles.fieldLabel}>Logo</label>
            <div className={styles.logoUpload}>
              <Upload size={16} />
              <span>Upload logo (SVG, PNG — max 2MB)</span>
            </div>
          </div>
          <div className={styles.brandingField}>
            <label className={styles.fieldLabel}>Favicon</label>
            <div className={styles.logoUpload}>
              <Upload size={16} />
              <span>Upload favicon (ICO, PNG 32×32)</span>
            </div>
          </div>
        </div>

        <div className={styles.brandingCard}>
          <div className={styles.brandingCardTitle}>Color Theme</div>
          <div className={styles.brandingField}>
            <label className={styles.fieldLabel}>Primary Brand Color</label>
            <div className={styles.colorPickerRow}>
              <input
                type="color"
                className={styles.colorPicker}
                value={primaryColor}
                onChange={e => setPrimaryColor(e.target.value)}
              />
              <input
                className={styles.fieldInput}
                value={primaryColor}
                onChange={e => setPrimaryColor(e.target.value)}
                style={{ fontFamily: 'var(--aptean-font-mono)' }}
              />
            </div>
          </div>

          <div className={styles.brandingField}>
            <label className={styles.fieldLabel}>Default Theme</label>
            <select className={styles.fieldInput}>
              <option value="light">Light Mode</option>
              <option value="dark">Dark Mode</option>
              <option value="system">Follow System</option>
            </select>
          </div>

          <div className={styles.themePreview} style={{ '--preview-accent': primaryColor } as React.CSSProperties}>
            <div className={styles.previewSidebar}>
              <div className={styles.previewDot} />
              <div className={styles.previewDot} />
              <div className={styles.previewDotActive} />
            </div>
            <div className={styles.previewMain}>
              <div className={styles.previewCard} />
              <div className={styles.previewCard} />
            </div>
          </div>
        </div>

        <div className={styles.brandingCard}>
          <div className={styles.brandingCardTitle}>Portal Settings</div>
          {[
            { label: 'Show powered-by Aptean footer',    def: true  },
            { label: 'Enable dark mode toggle for users',def: true  },
            { label: 'Show product version badge',       def: false },
            { label: 'Enable help tooltips',             def: true  },
            { label: 'Custom domain (white-label)',      def: false },
          ].map(item => (
            <label key={item.label} className={styles.settingToggleItem}>
              <span>{item.label}</span>
              <input type="checkbox" defaultChecked={item.def} className={styles.checkbox} />
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ─── Platform Config Tab — full product + size CRUD ────────────────────── */
const PRODUCT_FAMILIES = [
  { value: 'appcentral',    label: 'AppCentral' },
  { value: 'aip',          label: 'Aptean Integration Platform (AIP)' },
  { value: 'pay_edi',      label: 'Pay EDI' },
  { value: 'm2m',          label: 'Made2Manage (M2M)' },
  { value: 'agent_central',label: 'Agent Central' },
  { value: 'genai',        label: 'GenAI' },
  { value: 'fixed_assets', label: 'Fixed Assets' },
  { value: 'apprise',      label: 'Apprise' },
  { value: 'custom',       label: 'Custom' },
]
const T_SHIRT_SIZES = [
  { value: 'S',  label: 'Small (S)' },
  { value: 'M',  label: 'Medium (M)' },
  { value: 'L',  label: 'Large (L)' },
  { value: 'XL', label: 'Extra Large (XL)' },
]

function PlatformConfigTab() {
  const qc = useQueryClient()
  const [expandedProduct, setExpandedProduct] = useState<number | null>(null)
  const [productModal, setProductModal] = useState<{ open: boolean; editing: any }>({ open: false, editing: null })
  const [sizeModal, setSizeModal]     = useState<{ open: boolean; editing: any; productId: number | null }>({ open: false, editing: null, productId: null })
  const [productForm, setProductForm] = useState<Record<string, any>>({})
  const [sizeForm, setSizeForm]       = useState<Record<string, any>>({})
  const [deletingProduct, setDeletingProduct] = useState<number | null>(null)
  const [deletingSize, setDeletingSize]       = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [productError, setProductError] = useState<string | null>(null)
  const [sizeError, setSizeError] = useState<string | null>(null)
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['products-admin'],
    queryFn: () => api.get('/products/').then(r => r.data),
  })
  const { data: sizesData } = useQuery({
    queryKey: ['size-configs'],
    queryFn: () => api.get('/products/sizes/').then(r => r.data),
  })
  const productList: any[] = Array.isArray(productsData) ? productsData : (productsData?.results ?? [])
  const sizeList: any[]    = Array.isArray(sizesData)    ? sizesData    : (sizesData?.results    ?? [])
  // Apprise sizes are defined by the VM profile only (the generic CPU / replica / AKS fields are not shown)
  const isAppriseModal = isAppriseProduct(productList.find((p) => p.id === sizeModal.productId))

  /** Refresh every cached copy of the catalog: the admin lists AND the New Environment wizard's own queries. */
  function refreshCatalog() {
    qc.invalidateQueries({ queryKey: ['products-admin'] })
    qc.invalidateQueries({ queryKey: ['size-configs'] })
    qc.invalidateQueries({ queryKey: productKeys.all })
  }

  function openProductModal(product?: any) {
    setProductError(null)
    setProductForm(product ? {
      name: product.name, slug: product.slug,
      description: product.description ?? '',
      pipeline_url: product.pipeline_url ?? '',
      is_active: product.is_active,
    } : { is_active: true, description: '', pipeline_url: '' })
    setProductModal({ open: true, editing: product ?? null })
  }

  function closeProductModal() { setProductError(null); setProductModal({ open: false, editing: null }) }

  function openSizeModal(productId: number, size?: any) {
    setSizeForm(size ? {
      size: size.size,
      cpu_request:          size.resources.cpu_request,
      cpu_limit:            size.resources.cpu_limit,
      memory_request:       size.resources.memory_request,
      memory_limit:         size.resources.memory_limit,
      min_replicas:         size.resources.min_replicas,
      max_replicas:         size.resources.max_replicas,
      estimated_cost_usd:   size.resources.estimated_monthly_cost_usd ?? '',
      storage_class:        size.resources.storage_class ?? '',
      // Azure VM infra sizing (Apprise on a VM) — separate from the app sizing above. Also carries the shared
      // max_users input (blank when not set) that the generic and the Apprise editor both write.
      ...vmFormFromResources(size.resources.vm, size.resources.max_users),
    } : { cpu_request: '250m', cpu_limit: '500m', memory_request: '512Mi', memory_limit: '1Gi', min_replicas: 1, max_replicas: 3, storage_class: '', ...vmFormFromResources(null, null) })
    setSizeError(null)
    setSizeModal({ open: true, editing: size ?? null, productId })
  }

  function closeSizeModal() { setSizeModal({ open: false, editing: null, productId: null }) }

  async function saveProduct() {
    setSaving(true)
    setProductError(null)
    try {
      if (productModal.editing) {
        await api.patch(`/products/${productModal.editing.id}/`, productForm)
      } else {
        await api.post('/products/', productForm)
      }
      refreshCatalog()
      closeProductModal()
    } catch (err: any) {
      const data = err?.response?.data
      let msg = 'Failed to save product. Please try again.'
      if (data !== null && data !== undefined) {
        if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) {
          msg = data.trim()
        } else if (typeof data === 'object') {
          const parts = Object.entries(data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : String(v)}`)
          if (parts.length) msg = parts.join(' | ')
        }
      }
      setProductError(msg)
    } finally {
      setSaving(false)
    }
  }

  async function deleteProduct(id: number) {
    await api.delete(`/products/${id}/`)
    refreshCatalog()
    setDeletingProduct(null)
    if (expandedProduct === id) setExpandedProduct(null)
  }

  async function saveSize() {
    const vmValues = Object.fromEntries(VM_FORM_KEYS.map((k) => [k, sizeForm[k]])) as unknown as VmSizingFormValues
    const vmError = validateVmSizingForm(vmValues, { apprise: isAppriseModal })
    if (vmError) { setSizeError(vmError); return }
    const base = Object.fromEntries(Object.entries(sizeForm).filter(([k]) => !(VM_FORM_KEYS as readonly string[]).includes(k)))
    // The Apprise editor has no storage class box (its VM size is the SKU), and the API refuses a blank one: leave it
    // out so the backend default applies (an edit keeps the stored value).
    if (isAppriseModal && !String(base.storage_class ?? '').trim()) delete base.storage_class
    const payload = { ...base, ...vmPayloadFromForm(vmValues, { apprise: isAppriseModal }) }
    setSaving(true)
    setSizeError(null)
    try {
      // Max users is optional: an empty input clears it (null).
      const body = { ...payload, max_users: parseMaxUsersInput(sizeForm.max_users).value }
      if (sizeModal.editing) {
        await api.patch(`/products/sizes/${sizeModal.editing.id}/`, body)
      } else {
        await api.post('/products/sizes/', { ...body, product: sizeModal.productId })
      }
      refreshCatalog()
      closeSizeModal()
    } catch (err: any) {
      const data = err?.response?.data
      setSizeError(data && typeof data === 'object'
        ? Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : String(v)}`).join(' | ')
        : 'Failed to save size config. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteSize(id: number) {
    await api.delete(`/products/sizes/${id}/`)
    refreshCatalog()
    setDeletingSize(null)
  }

  const pf = productForm
  const sf = sizeForm
  const maxUsersError = parseMaxUsersInput(sf.max_users).error

  return (
    <div className={styles.tabContent}>

      {/* ── Products list ── */}
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}>Products</div>
          <div className={styles.tabDesc}>Manage Aptean products available for deployment</div>
        </div>
        <button className={styles.btnPrimary} onClick={() => openProductModal()}>
          <Plus size={14} /> Add Product
        </button>
      </div>

      <div className={styles.tileGroup}>
        <div className={styles.tileTableHeader} style={{ gridTemplateColumns: '1.6fr 1fr 0.7fr 80px 80px auto' }}>
          <span>Product</span><span>Family</span><span>Version</span><span>Status</span><span>Sizes</span><span>Actions</span>
        </div>

        {productsLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles.tileRow} style={{ height: 44, background: 'var(--aptean-bg-elevated)', gridTemplateColumns: '1.6fr 1fr 0.7fr 80px 80px auto' }} />
            ))
          : productList.length === 0
            ? <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--aptean-text-muted)', fontSize: '0.875rem' }}>
                No products yet — click Add Product to create the first one.
              </div>
            : productList.map((product: any) => {
                const productSizes = sizeList.filter((s: any) => s.product === product.id)
                // Traverse and Process Pro are sized by the RDS pool tiers shown under them (read-only), not from stored size configs.
                const isCatalogProduct = usesPoolTiers(product)
                const sizeCount = isCatalogProduct ? POOL_TIERS.length : productSizes.length
                const isExpanded   = expandedProduct === product.id
                return (
                  <div key={product.id}>
                    {/* product row */}
                    <div
                      className={styles.tileRow}
                      style={{ gridTemplateColumns: '1.6fr 1fr 0.7fr 80px 80px auto', cursor: 'pointer' }}
                      onClick={() => setExpandedProduct(isExpanded ? null : product.id)}
                    >
                      <span className={styles.tileRowName}>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {product.name}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--aptean-text-secondary)' }}>
                        {PRODUCT_FAMILIES.find(f => f.value === product.family)?.label ?? product.family}
                      </span>
                      <span className={styles.sizeMono}>{product.version}</span>
                      <span className={`${styles.toggleBtn} ${product.is_active ? styles.toggleOn : styles.toggleOff}`}>
                        {product.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--aptean-text-muted)' }}>
                        {isCatalogProduct ? `${sizeCount} pool tiers` : `${sizeCount} size${sizeCount !== 1 ? 's' : ''}`}
                      </span>
                      <div className={styles.tileRowActions} onClick={e => e.stopPropagation()}>
                        {deletingProduct === product.id
                          ? <div className={styles.deleteConfirm}>
                              <span className={styles.deleteConfirmText}>Delete?</span>
                              <button className={styles.btnConfirmDelete} onClick={() => deleteProduct(product.id)}>Yes</button>
                              <button className={styles.btnCancelDelete} onClick={() => setDeletingProduct(null)}>No</button>
                            </div>
                          : <>
                              <button className={styles.iconActionBtn} onClick={() => openProductModal(product)}><Edit3 size={13} /></button>
                              <button className={`${styles.iconActionBtn} ${styles.iconDanger}`} onClick={() => setDeletingProduct(product.id)}><Trash2 size={13} /></button>
                            </>
                        }
                      </div>
                    </div>

                    {/* expanded size configs */}
                    {isExpanded && (
                      <div style={{ background: 'var(--aptean-bg-base)', borderBottom: '1px solid var(--aptean-border-muted)' }}>
                        {/* Traverse and Process Pro: their RDS pool tiers, read-only, are what the deployment wizard offers and builds. */}
                        {isCatalogProduct && <ProductPoolTiers productId={product.id} />}
                        {!isCatalogProduct && (<>
                        <div style={{ padding: '8px 16px 4px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--aptean-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            T-Shirt Size Configs
                          </span>
                          <button className={styles.btnSecondary} style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                            onClick={() => openSizeModal(product.id)}>
                            <Plus size={12} /> Add Size
                          </button>
                        </div>
                        {productSizes.length === 0
                          ? <div style={{ padding: '6px 40px 14px', fontSize: '0.8125rem', color: 'var(--aptean-text-muted)' }}>
                              No size configs — click Add Size to configure resource allocations.
                            </div>
                          : <div className={styles.sizeTable} style={{ margin: '4px 16px 12px 40px' }}>
                              <div className={styles.sizeTableHeader}>
                                {isAppriseProduct(product) ? (<>
                                  <span>Size</span><span></span>
                                  <span>VM size</span><span>vCPU / RAM</span>
                                  <span>Users</span><span>OS disk</span><span></span>
                                </>) : (<>
                                <span>Size</span><span>Users</span>
                                <span>CPU Req / Limit</span><span>Mem Req / Limit</span>
                                <span>Replicas</span><span>Cost/mo</span><span></span>
                                </>)}
                              </div>
                              {productSizes.map((cfg: any) => (
                                <div key={cfg.id} className={styles.sizeRow}>
                                  <span className={`${styles.sizeBadge} ${styles[`size${cfg.size}`]}`}>{cfg.size_display ?? cfg.size}</span>
                                  {isAppriseProduct(product) ? (<>
                                    <span />
                                    <span className={styles.sizeMono}>{cfg.resources.vm?.vm_size ?? 'Not set'}</span>
                                    <span className={styles.sizeMono}>{cfg.resources.vm ? `${cfg.resources.vm.vcpu} / ${cfg.resources.vm.memory_gb} GB` : '—'}</span>
                                    <span className={styles.sizeMono}>{readMaxUsers(cfg.resources.max_users) ?? '—'}</span>
                                    <span className={styles.sizeMono}>{cfg.resources.vm ? osDiskLabel(cfg.resources.vm.os_disk_gb) : '—'}</span>
                                  </>) : (<>
                                  <span className={styles.sizeMono} data-testid={`size-max-users-${cfg.id}`}
                                    title={readMaxUsers(cfg.resources.max_users) !== null ? `Max users: ${formatMaxUsers(cfg.resources.max_users)}` : undefined}>
                                    {readMaxUsers(cfg.resources.max_users) ?? ''}
                                  </span>
                                  <span className={styles.sizeMono}>{cfg.resources.cpu_request} / {cfg.resources.cpu_limit}</span>
                                  <span className={styles.sizeMono}>{cfg.resources.memory_request} / {cfg.resources.memory_limit}</span>
                                  <span className={styles.sizeMono}>{cfg.resources.min_replicas}–{cfg.resources.max_replicas}</span>
                                  <span className={styles.sizeCost}>${cfg.resources.estimated_monthly_cost_usd ?? 0}</span>
                                  </>)}
                                  {cfg.resources.vm && (
                                    <span className={styles.sizeMono} style={{ gridColumn: '1/-1', order: 99, fontSize: '0.75rem', color: 'var(--aptean-text-muted)' }}>
                                      {vmSummary(cfg.resources.vm)}
                                    </span>
                                  )}
                                  <div className={styles.tileRowActions}>
                                    {deletingSize === cfg.id
                                      ? <div className={styles.deleteConfirm}>
                                          <span className={styles.deleteConfirmText}>Delete?</span>
                                          <button className={styles.btnConfirmDelete} onClick={() => deleteSize(cfg.id)}>Yes</button>
                                          <button className={styles.btnCancelDelete} onClick={() => setDeletingSize(null)}>No</button>
                                        </div>
                                      : <>
                                          <button className={styles.iconActionBtn} aria-label={`Edit ${cfg.size_display ?? cfg.size} size of ${product.name}`} onClick={() => openSizeModal(product.id, cfg)}><Edit3 size={13} /></button>
                                          <button className={`${styles.iconActionBtn} ${styles.iconDanger}`} aria-label={`Delete ${cfg.size_display ?? cfg.size} size of ${product.name}`} onClick={() => setDeletingSize(cfg.id)}><Trash2 size={13} /></button>
                                        </>
                                    }
                                  </div>
                                </div>
                              ))}
                            </div>
                        }

                        </>)}

                        {/* VM image catalog — Apprise only (read-only) */}
                        {(product.slug === 'apprise' || product.family === 'apprise') && (
                          <div style={{ borderTop: '1px solid var(--aptean-border-muted)', padding: '16px 16px 20px 40px' }}>
                            <AppriseImageCatalog />
                          </div>
                        )}

                        {/* ERP versions — Ross only */}
                        {product.slug === 'ross' && (
                          <div style={{ borderTop: '1px solid var(--aptean-border-muted)', padding: '16px 16px 20px 40px' }}>
                            <ErpVersionsTab />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
        }
      </div>

      {/* ── Deployment Quotas ── */}
      <div className={styles.quotaSection}>
        <div className={styles.quotaSectionTitle}>Deployment Quotas</div>
        <div className={styles.quotaGrid}>
          {[
            { label: 'Max concurrent deployments', type: 'number', default: '5',   min: 1,  max: 20  },
            { label: 'Deployment timeout (minutes)',type: 'number', default: '120', min: 10, max: 480 },
            { label: 'Max deployments per tenant',  type: 'number', default: '10', min: 1,  max: 50  },
            { label: 'Auto-rollback on failure',    type: 'toggle', default: 'true' },
          ].map(q => (
            <div key={q.label} className={styles.quotaItem}>
              <label className={styles.quotaLabel}>{q.label}</label>
              {q.type === 'toggle'
                ? <input type="checkbox" defaultChecked className={styles.checkbox} />
                : <input type="number" className={styles.quotaInput} defaultValue={q.default} min={(q as any).min} max={(q as any).max} />
              }
            </div>
          ))}
        </div>
      </div>

      {/* ── Add / Edit Product Modal ── */}
      {productModal.open && (
        <div className={styles.modalOverlay} onClick={closeProductModal}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}><Package size={16} />{productModal.editing ? 'Edit Product' : 'Add Product'}</div>
              <button className={styles.modalClose} onClick={closeProductModal}><X size={16} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Name <span className={styles.requiredMark}>*</span></label>
                  <input className={styles.formInput} value={pf.name ?? ''} placeholder="e.g. AppCentral"
                    onChange={e => {
                      const name = e.target.value
                      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                      setProductForm(f => ({ ...f, name, ...(!productModal.editing && { slug }) }))
                    }} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Slug <span className={styles.requiredMark}>*</span></label>
                  <input className={styles.formInput} value={pf.slug ?? ''} placeholder="e.g. appcentral"
                    onChange={e => setProductForm(f => ({ ...f, slug: e.target.value }))} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Description <span className={styles.optionalHint}>(optional)</span></label>
                <input className={styles.formInput} value={pf.description ?? ''} placeholder="Short description"
                  onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Pipeline URL <span className={styles.optionalHint}>(optional)</span></label>
                <input className={styles.formInput} value={pf.pipeline_url ?? ''} placeholder="https://api.github.com/repos/org/repo/actions/workflows/deploy.yml/dispatches"
                  onChange={e => setProductForm(f => ({ ...f, pipeline_url: e.target.value }))} />
                <div className={styles.formHint}>CI/CD pipeline webhook — triggered automatically when this product is deployed.</div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Status</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={pf.is_active ?? true}
                    onChange={e => setProductForm(f => ({ ...f, is_active: e.target.checked }))} />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--aptean-text-secondary)' }}>Active (visible in deployment catalog)</span>
                </label>
              </div>
            </div>
            {productError && (
              <div style={{ padding: '8px 16px', fontSize: '0.8125rem', color: 'var(--aptean-error)', background: 'rgba(var(--aptean-error-rgb,220,53,69),0.08)', borderTop: '1px solid var(--aptean-border-muted)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>{productError}</span>
              </div>
            )}
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={closeProductModal}>Cancel</button>
              <button className={styles.btnPrimary} disabled={saving || !pf.name || !pf.slug} onClick={saveProduct}>
                <Save size={14} /> {saving ? 'Saving…' : productModal.editing ? 'Save Changes' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Size Config Modal ── */}
      {sizeModal.open && (
        <div className={styles.modalOverlay} onClick={closeSizeModal}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}><Settings size={16} />{sizeModal.editing ? 'Edit Size Config' : 'Add Size Config'}</div>
              <button className={styles.modalClose} onClick={closeSizeModal}><X size={16} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Size <span className={styles.requiredMark}>*</span></label>
                <select className={styles.formSelect} value={sf.size ?? ''} disabled={!!sizeModal.editing}
                  onChange={e => setSizeForm(f => ({ ...f, size: e.target.value }))}>
                  <option value="">Select size…</option>
                  {T_SHIRT_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              {!isAppriseModal && (<>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>CPU Request</label>
                  <input className={styles.formInput} value={sf.cpu_request ?? ''} placeholder="250m"
                    onChange={e => setSizeForm(f => ({ ...f, cpu_request: e.target.value }))} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>CPU Limit</label>
                  <input className={styles.formInput} value={sf.cpu_limit ?? ''} placeholder="500m"
                    onChange={e => setSizeForm(f => ({ ...f, cpu_limit: e.target.value }))} />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Memory Request</label>
                  <input className={styles.formInput} value={sf.memory_request ?? ''} placeholder="512Mi"
                    onChange={e => setSizeForm(f => ({ ...f, memory_request: e.target.value }))} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Memory Limit</label>
                  <input className={styles.formInput} value={sf.memory_limit ?? ''} placeholder="1Gi"
                    onChange={e => setSizeForm(f => ({ ...f, memory_limit: e.target.value }))} />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Min Replicas</label>
                  <input className={styles.formInput} type="number" min={1} value={sf.min_replicas ?? ''} placeholder="1"
                    onChange={e => setSizeForm(f => ({ ...f, min_replicas: Number(e.target.value) }))} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Max Replicas</label>
                  <input className={styles.formInput} type="number" min={1} value={sf.max_replicas ?? ''} placeholder="3"
                    onChange={e => setSizeForm(f => ({ ...f, max_replicas: Number(e.target.value) }))} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Estimated Cost USD/month <span className={styles.optionalHint}>(optional)</span></label>
                <input className={styles.formInput} type="number" min={0} value={sf.estimated_cost_usd ?? ''} placeholder="0.00"
                  onChange={e => setSizeForm(f => ({ ...f, estimated_cost_usd: e.target.value }))} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Azure VM SKU <span className={styles.optionalHint}>(optional — e.g. Standard_D4s_v3)</span></label>
                <input className={styles.formInput} value={sf.storage_class ?? ''} placeholder="Standard_D4s_v3"
                  onChange={e => setSizeForm(f => ({ ...f, storage_class: e.target.value }))} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="size-max-users">Max users <span className={styles.optionalHint}>(optional)</span></label>
                <input id="size-max-users" className={styles.formInput} type="number" min={1} step={1} value={sf.max_users ?? ''} placeholder="e.g. 25"
                  data-testid="size-max-users" aria-invalid={maxUsersError ? true : undefined}
                  onChange={e => setSizeForm(f => ({ ...f, max_users: e.target.value }))} />
                <div className={styles.formHint} style={maxUsersError ? { color: 'var(--aptean-error)' } : undefined} data-testid="size-max-users-hint">
                  {maxUsersError ?? 'How many users can log in at this size (informational). Shown on the WorkWise size cards.'}
                </div>
              </div>
              </>)}
              {/* Azure VM profile — the whole editor for Apprise sizes (and only for them) */}
              {isAppriseModal && <AdminVmSizingBlock
                values={Object.fromEntries(VM_FORM_KEYS.map((k) => [k, sf[k]])) as unknown as VmSizingFormValues}
                onChange={(patch) => setSizeForm(f => ({ ...f, ...patch }))}
                derived={sizeModal.editing?.resources?.vm?.derived ? sizeModal.editing.resources.vm : undefined}
              />}
              {sizeError && <div style={{ color: 'var(--aptean-error)', fontSize: '0.8125rem' }}>{sizeError}</div>}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={closeSizeModal}>Cancel</button>
              <button className={styles.btnPrimary} disabled={saving || !sf.size || (!isAppriseModal && !!maxUsersError)} onClick={saveSize}>
                <Save size={14} /> {saving ? 'Saving…' : sizeModal.editing ? 'Save Changes' : 'Add Size'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Deploy Templates Tab ───────────────────────────────────────────────── */
function TemplatesTab() {
  const templates = [
    { id: '1', name: 'AppCentral Full Stack',    products: 'IAM, AppCentral, Embedded BI', env: 'Production',  strategy: 'blue_green', lastUsed: '2026-04-08' },
    { id: '2', name: 'AIP Platform Stack',       products: 'AIP, Agent Central, GenAI',    env: 'Staging',     strategy: 'rolling',    lastUsed: '2026-04-05' },
    { id: '3', name: 'Pay EDI Standalone',       products: 'Pay EDI',                       env: 'QA',          strategy: 'canary',     lastUsed: '2026-03-28' },
    { id: '4', name: 'M2M Manufacturing Suite',  products: 'M2M, Fixed Assets, Pay EDI',   env: 'Production',  strategy: 'blue_green', lastUsed: '2026-03-20' },
    { id: '5', name: 'Dev Sandbox Quick Start',  products: 'AppCentral',                   env: 'Dev',         strategy: 'recreate',   lastUsed: '2026-04-07' },
  ]

  return (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <div>
          <div className={styles.tabTitle}>Deployment Templates</div>
          <div className={styles.tabDesc}>Pre-configured templates for common deployment patterns</div>
        </div>
        <div className={styles.tabActions}>
          <button className={styles.btnSecondary}><Upload size={14} /> Import</button>
          <button className={styles.btnPrimary}><Plus size={14} /> New Template</button>
        </div>
      </div>

      <div className={styles.templateList}>
        {templates.map(t => (
          <div key={t.id} className={styles.templateCard}>
            <div className={styles.templateIcon}><Package size={20} /></div>
            <div className={styles.templateBody}>
              <div className={styles.templateName}>{t.name}</div>
              <div className={styles.templateProducts}>{t.products}</div>
              <div className={styles.templateMeta}>
                <span className={styles.envChip}>{t.env}</span>
                <span className={styles.strategyChip}>{t.strategy}</span>
                <span className={styles.templateDate}>Last used: {t.lastUsed}</span>
              </div>
            </div>
            <div className={styles.templateActions}>
              <button className={styles.btnSecondary}><Download size={13} /> Export</button>
              <button className={styles.btnPrimary}><Rocket size={13} /> Use</button>
              <button className={styles.iconActionBtn}><Edit3 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── ERP Versions Tab ───────────────────────────────────────────────────── */
function ErpVersionsTab() {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const { data: versions, isLoading } = useQuery({
    queryKey: ['erp-versions'],
    queryFn: async () => {
      const res = await deploymentsApi.getErpVersions()
      return res.data
    },
  })

  const handleAdd = async () => {
    if (!name.trim()) {
      setError('Version name is required')
      return
    }
    setSaving(true)
    setError('')
    try {
      const trimmed = name.trim()
      await deploymentsApi.createErpVersion({ name: trimmed, display_name: trimmed, major_version: trimmed })
      queryClient.invalidateQueries({ queryKey: ['erp-versions'] })
      setName('')
    } catch {
      setError('Failed to save — check your admin permissions')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deploymentsApi.deleteErpVersion(id)
      queryClient.invalidateQueries({ queryKey: ['erp-versions'] })
    } catch {
      setError('Failed to delete — check your admin permissions')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>ERP Version Catalogue</h3>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--aptean-text-secondary)' }}>
          Versions listed here appear in the New Environment deployment wizard.
        </p>
      </div>

      {/* Add new version form */}
      <div style={{ background: 'var(--aptean-bg-elevated)', border: '1px solid var(--aptean-border)', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600 }}>Add New Version</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: 'var(--aptean-text-secondary)' }}>Version Name *</label>
            <input
              style={{ width: '100%', padding: '8px 10px', background: 'var(--aptean-bg)', border: '1px solid var(--aptean-border)', borderRadius: 6, color: 'var(--aptean-text-primary)', fontSize: 13, boxSizing: 'border-box' }}
              placeholder="e.g. 82"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={saving}
            style={{ padding: '8px 16px', background: 'var(--aptean-teal)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
        {error && <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--aptean-red, #e05252)' }}>{error}</p>}
      </div>

      {/* Version list */}
      {isLoading ? (
        <p style={{ color: 'var(--aptean-text-muted)', fontSize: 13 }}>Loading versions…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--aptean-border)' }}>
              {['Name', 'Status', 'Created', ''].map((h, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--aptean-text-secondary)', fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(versions ?? []).map((v: ErpVersion) => (
              <tr key={v.id} style={{ borderBottom: '1px solid var(--aptean-border)' }}>
                <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>{v.name}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: v.is_active ? 'rgba(20,184,166,0.15)' : 'rgba(156,163,175,0.2)', color: v.is_active ? 'var(--aptean-teal)' : 'var(--aptean-text-muted)' }}>
                    {v.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--aptean-text-muted)' }}>{new Date(v.created_at).toLocaleDateString()}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  {deletingId === v.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: 12, color: 'var(--aptean-text-secondary)' }}>Delete?</span>
                      <button onClick={() => handleDelete(v.id)} className={styles.btnConfirmDelete}>Yes</button>
                      <button onClick={() => setDeletingId(null)} className={styles.btnCancelDelete}>No</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeletingId(v.id)}
                      className={`${styles.iconActionBtn} ${styles.iconDanger}`}
                      title="Delete version"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(versions ?? []).length === 0 && (
              <tr><td colSpan={4} style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--aptean-text-muted)' }}>No ERP versions configured yet. Add the first one above.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

/* ─── Main Admin Page ────────────────────────────────────────────────────── */
export function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('tiles')

  return (
    <div className={styles.page}>
      {/* Left panel */}
      <aside className={styles.adminSidebar}>
        <div className={styles.adminSidebarTitle}>Admin Console</div>
        <div className={styles.adminSidebarDesc}>Platform configuration and management</div>

        <nav className={styles.adminNav}>
          {TABS.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                className={`${styles.adminNavItem} ${activeTab === tab.id ? styles.adminNavActive : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={16} />
                <div className={styles.adminNavText}>
                  <div className={styles.adminNavLabel}>{tab.label}</div>
                  <div className={styles.adminNavDesc}>{tab.desc}</div>
                </div>
              </button>
            )
          })}
        </nav>
      </aside>

      {/* Main content */}
      <main className={styles.adminMain}>
        {activeTab === 'tiles'         && <TileManagementTab />}
        {activeTab === 'rbac'          && <RBACTab />}
        {activeTab === 'workflows'     && <WorkflowsTab />}
        {activeTab === 'templates'     && <TemplatesTab />}
        {activeTab === 'secrets'       && <SecretsTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'branding'      && <BrandingTab />}
        {activeTab === 'platform'      && <PlatformConfigTab />}
      </main>
    </div>
  )
}

